import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, Timer, FolderOpen, LogOut, Home, Footprints, Lock, BellRing, Coffee, Play, Loader2, UserCog, LayoutGrid, BriefcaseBusiness } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { toast } from 'sonner';
import { EaserLogin } from '../components/easer/EaserLogin';
import { EaserPlanning } from '../components/easer/EaserPlanning';
import { EaserPointage } from '../components/easer/EaserPointage';
import { EaserDocuments } from '../components/easer/EaserDocuments';
import { EaserTracts } from '../components/easer/EaserTracts';
import { EaserGrh, GRH_SUBTABS } from '../components/easer/EaserGrh';
import { EaserCrm, CRM_SUBTABS } from '../components/easer/crm/EaserCrm';
import { EaserHome } from '../components/easer/EaserHome';
import { NotificationPopup, NotificationBanners } from '../components/easer/NotificationPopup';
import { OfflineQueueBanner } from '../components/easer/OfflineQueueBanner';
import { DolibarrStatusBanner } from '../components/easer/DolibarrStatusBanner';
import { InstallAppCard } from '../components/InstallAppCard';
import { getToken, getStoredUser, clearSession, easerGet, storeSession, fmtTime } from '../lib/easerApi';
import { subscribeQueue, pendingClockEndIds, pendingClockStarts, pendingClockPauses, pendingClockResumes, pendingClockEventIds } from '../lib/offlineQueue';
import { clockSlot } from '../lib/easerClock';
import { countNewDocs } from '../lib/docsSeen';
import { popupCandidates, bannerCandidates, onAppStart } from '../lib/easerNotifications';
import { syncPushSubscription } from '../lib/push';
import { resetAcc, resumeIfActive } from '../lib/accTracker';
import { AccBanner } from '../components/easer/AccBanner';

const LOGO = 'https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png';

const BASE_TABS = [
  { id: 'accueil', label: 'Accueil', icon: LayoutGrid },
  { id: 'planning', label: 'Planning', icon: CalendarDays },
  { id: 'pointage', label: 'Pointage', icon: Timer },
  { id: 'tracts', label: 'Tracts', icon: Footprints },
  { id: 'documents', label: 'Documents', icon: FolderOpen },
  { id: 'grh', label: 'GRH', icon: UserCog },
];
// Onglet « CRM » (tableau de bord, clients, devis Green, contrôles qualité, suivi) : réservé aux Easers portant le tag Dolibarr « MANAGER » (vérifié aussi côté serveur).
const CRM_TAB = { id: 'crm', label: 'CRM', icon: BriefcaseBusiness };
export const isManager = (user) => (user?.tags || []).some((t) => String(t).trim().toLowerCase() === 'manager');
// Old direct tabs (frais, conges → GRH ; devis / manager → CRM) : keep old links working.
const LEGACY_TABS = { frais: { tab: 'grh', sub: 'frais' }, conges: { tab: 'grh', sub: 'conges' }, devis: { tab: 'crm', sub: 'devis' }, manager: { tab: 'crm', sub: 'devis' } };
const LEGACY_SUBS = { qualite: 'controles' };

export default function EaserPage() {
  const [user, setUser] = useState(() => (getToken() ? getStoredUser() : null));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const rawTab = params.get('tab');
  const TABS = isManager(user) ? [...BASE_TABS, CRM_TAB] : BASE_TABS;
  const legacy = LEGACY_TABS[rawTab];
  const requestedTab = TABS.some((t) => t.id === rawTab) ? rawTab : legacy && TABS.some((t) => t.id === legacy.tab) ? legacy.tab : 'accueil';
  const grhSub = GRH_SUBTABS.some((t) => t.id === params.get('sub')) ? params.get('sub') : (legacy?.tab === 'grh' ? legacy.sub : 'frais');
  const rawSub = LEGACY_SUBS[params.get('sub')] || params.get('sub');
  const crmSub = CRM_SUBTABS.some((t) => t.id === rawSub) ? rawSub : (legacy?.tab === 'crm' ? legacy.sub : 'dashboard');
  const [notifications, setNotifications] = useState(null); // agency notifications kept for this Easer (GRH > Notifications)
  const [bannerHidden, setBannerHidden] = useState([]); // banner ids closed during this session
  const [active, setActive] = useState(null); // intervention currently clocked in (Dolibarr), locks the rest of the portal
  const [pendingEnds, setPendingEnds] = useState([]); // event ids whose "fin" waits in the offline queue
  const [pendingStarts, setPendingStarts] = useState([]); // [{eventId,label,at}] starts waiting in the offline queue
  const [pendingPauses, setPendingPauses] = useState([]); // [{eventId,label,at}] pauses waiting in the offline queue
  const [pendingResumes, setPendingResumes] = useState([]); // [{eventId,label,at}] resumes waiting in the offline queue
  const [pendingClockIds, setPendingClockIds] = useState([]); // slot ids with any pointage waiting on the phone
  const [paused, setPaused] = useState(null); // pause in progress (portal state: FIN clocked, resume will clock a new DEBUT)
  const [pauseUsed, setPauseUsed] = useState([]); // slot ids whose single pause is already taken
  const [pauseOverdue, setPauseOverdue] = useState(false); // pause longer than 30 min
  const [resuming, setResuming] = useState(false);
  const [overdue, setOverdue] = useState(false); // prestation still open 30 min after its planned end
  const [newDocs, setNewDocs] = useState(0); // documents not yet seen on this device -> red dot on the tab

  const logout = useCallback((silent = false) => {
    resetAcc(); // arrête un éventuel traceur d'accompagnement (le trajet reste ouvert côté serveur, repris à la reconnexion)
    clearSession();
    setUser(null);
    if (!silent) toast.info('Vous êtes déconnecté');
  }, []);

  const onUnauthorized = useCallback(() => {
    resetAcc();
    clearSession();
    setUser(null);
    toast.error('Session expirée, reconnectez-vous');
  }, []);

  // Refresh the profile silently when a session exists (also validates the token).
  useEffect(() => {
    if (!getToken()) return;
    easerGet('/api/easer/me')
      .then((r) => { setUser(r.data); storeSession(getToken(), r.data); })
      .catch((err) => { if (err?.response?.status === 401) onUnauthorized(); });
  }, [onUnauthorized]);

  // "Intervention en cours": refreshed on mount, every minute and after each pointage (event dispatched by the planning / queue).
  const refreshActive = useCallback(() => {
    if (!getToken()) return;
    easerGet('/api/easer/active')
      .then((r) => { setActive(r.data.active); setPaused(r.data.paused || null); setPauseUsed(r.data.pause_used || []); })
      .catch((err) => { if (err?.response?.status === 401) onUnauthorized(); });
  }, [onUnauthorized]);

  useEffect(() => {
    if (!user) return undefined;
    refreshActive();
    resumeIfActive(); // accompagnement GPS (bouton ACC) encore ouvert côté serveur : reprise du traceur + bandeau
    const timer = setInterval(refreshActive, 60000);
    window.addEventListener('easer:clock-changed', refreshActive);
    window.addEventListener('online', refreshActive);
    window.addEventListener('easer:dolibarr-restored', refreshActive);
    return () => { clearInterval(timer); window.removeEventListener('easer:clock-changed', refreshActive); window.removeEventListener('online', refreshActive); window.removeEventListener('easer:dolibarr-restored', refreshActive); };
  }, [user, refreshActive]);

  useEffect(() => subscribeQueue((items) => {
    setPendingEnds(pendingClockEndIds(items)); setPendingStarts(pendingClockStarts(items));
    setPendingPauses(pendingClockPauses(items)); setPendingResumes(pendingClockResumes(items)); setPendingClockIds(pendingClockEventIds(items));
  }), []);

  // Agency notifications: loaded on login, every 10 min and after each read (badge on GRH, pop-ups, banners).
  const refreshNotifications = useCallback(() => {
    if (!getToken()) return;
    easerGet('/api/easer/notifications')
      .then((r) => setNotifications(r.data.items || []))
      .catch((err) => { if (err?.response?.status === 401) onUnauthorized(); else setNotifications((n) => n || []); });
  }, [onUnauthorized]);

  useEffect(() => { if (user) syncPushSubscription(); }, [user]); // keep this phone's push subscription attached to the account

  useEffect(() => {
    if (!user) return undefined;
    refreshNotifications();
    const timer = setInterval(refreshNotifications, 10 * 60000);
    window.addEventListener('easer:notifications-changed', refreshNotifications);
    return () => { clearInterval(timer); window.removeEventListener('easer:notifications-changed', refreshNotifications); };
  }, [user, refreshNotifications]);

  const onNotificationRead = useCallback((id, readAt) => {
    setNotifications((items) => (items || []).map((n) => (n.id === id ? { ...n, read_at: readAt || n.read_at || new Date().toISOString() } : n)));
  }, []);

  // Pop-ups at the start of the app (one after the other); the queue is fixed at the first load so a read does not reshuffle it.
  // An installed app can stay open for days: a "new start" (back after 30+ min) rebuilds the queue and reopens closed banners.
  const [popupQueue, setPopupQueue] = useState(null);
  useEffect(() => {
    if (notifications && popupQueue === null) setPopupQueue(popupCandidates(notifications));
  }, [notifications, popupQueue]);
  useEffect(() => onAppStart(() => { setPopupQueue(null); setBannerHidden([]); refreshNotifications(); }), [refreshNotifications]);
  const unreadNotifications = (notifications || []).filter((n) => !n.read_at).length;
  const banners = bannerCandidates(notifications || []).filter((n) => !bannerHidden.includes(n.id) && !(popupQueue || []).some((p) => p.id === n.id && !n.read_at));

  // New documents on the Dolibarr user card: checked on login, every 10 min, and when the Documents tab is viewed.
  useEffect(() => {
    if (!user) return undefined;
    let docs = [];
    const check = () => easerGet('/api/easer/documents').then((r) => { docs = r.data.items || []; setNewDocs(countNewDocs(user.id, docs)); }).catch(() => {});
    const recount = () => setNewDocs(countNewDocs(user.id, docs));
    check();
    const timer = setInterval(check, 10 * 60000);
    window.addEventListener('easer:docs-seen', recount);
    return () => { clearInterval(timer); window.removeEventListener('easer:docs-seen', recount); };
  }, [user]);

  // A start (or a resume) kept on the phone locks the portal exactly like a start recorded in Dolibarr.
  const localStart = pendingStarts[0];
  const localResume = pendingResumes[0];
  const localPause = pendingPauses[0];
  let effectiveActive = active || (localStart ? { id: localStart.eventId, label: localStart.label, since: localStart.at, client_name: '', local: true } : null);
  if (!effectiveActive && localResume) effectiveActive = { id: localResume.eventId, label: localResume.label, since: localResume.at, client_name: paused?.client_name || '', local: true };
  // A pause kept on the phone lifts the lock exactly like a pause recorded in Dolibarr (unless its resume waits too).
  if (effectiveActive && localPause && localPause.eventId === effectiveActive.id && !pendingResumes.some((r) => r.eventId === effectiveActive.id)) effectiveActive = null;
  let effectivePaused = paused || (localPause ? { id: `local-${localPause.eventId}`, event_id: localPause.eventId, label: localPause.label, started_at: localPause.at, client_name: '', local: true } : null);
  if (effectivePaused && pendingResumes.some((r) => r.eventId === effectivePaused.event_id)) effectivePaused = null;
  // The lock is lifted locally as soon as the end is clocked, even if it still waits for the network.
  const locked = !!effectiveActive && !pendingEnds.includes(effectiveActive.id);
  const pausedId = effectivePaused?.id;
  const pausedEventId = effectivePaused?.event_id;
  const pausedLabel = effectivePaused?.label;
  const pausedSince = effectivePaused?.started_at;

  // Reminder: planned end + 30 min passed and the prestation is still open -> in-app alert + phone notification.
  useEffect(() => {
    const compute = () => setOverdue(!!(locked && active?.end && Date.now() > new Date(active.end).getTime() + 30 * 60000));
    compute();
    const t = setInterval(compute, 60000);
    return () => clearInterval(t);
  }, [locked, active]);

  useEffect(() => {
    if (!overdue || !active) return;
    const key = `easer_overdue_notified_${active.id}_${active.clock_in_event || ''}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    try { navigator.vibrate?.([200, 100, 200]); } catch { /* unsupported */ }
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try { new Notification('Fin de prestation à pointer', { body: `${active.label} est toujours en cours 30 min après la fin prévue.`, tag: key }); } catch { /* unsupported */ }
    }
  }, [overdue, active]);

  // Reminder: pause longer than 30 min -> in-app alert + phone notification (the agency cron also e-mails the Easer).
  useEffect(() => {
    const compute = () => setPauseOverdue(!!(pausedSince && Date.now() > new Date(pausedSince).getTime() + 30 * 60000));
    compute();
    const t = setInterval(compute, 60000);
    return () => clearInterval(t);
  }, [pausedSince]);

  useEffect(() => {
    if (!pauseOverdue || !pausedId) return;
    const key = `easer_pause_notified_${pausedId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    try { navigator.vibrate?.([200, 100, 200]); } catch { /* unsupported */ }
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try { new Notification('Pause de plus de 30 min', { body: `${pausedLabel} est en pause depuis plus de 30 minutes : reprenez la prestation puis pointez sa fin.`, tag: key }); } catch { /* unsupported */ }
    }
  }, [pauseOverdue, pausedId, pausedLabel]);

  useEffect(() => {
    if ((locked || pausedId) && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, [locked, pausedId]);

  const resumeFromBanner = async () => {
    if (!pausedEventId) return;
    setResuming(true);
    try {
      await clockSlot({ ev: { id: pausedEventId, label: pausedLabel }, action: 'resume', mustQueue: pendingClockIds.includes(pausedEventId), onUnauthorized });
      refreshActive();
    } finally {
      setResuming(false);
    }
  };
  // While a prestation is in progress only Accueil and Planning stay reachable (report + fin de prestation).
  const ALLOWED_LOCKED = ['accueil', 'planning'];
  const tab = locked && !ALLOWED_LOCKED.includes(requestedTab) ? 'planning' : requestedTab;

  const pickTab = (id) => {
    if (locked && !ALLOWED_LOCKED.includes(id)) {
      toast.warning(`Terminez d'abord la prestation en cours (${effectiveActive.label}) en pointant sa fin`, { id: 'easer-locked' });
      return;
    }
    setParams({ tab: id });
  };

  if (!user) {
    return (
      <>
        <SEOHead title="Espace Easer — Connexion" description="Accès réservé aux employés Ptitservices." path="/easer" noindex />
        <InstallAppCard portal="easer" headOnly />
        <EaserLogin onLogin={(u) => { setUser(u); navigate('/easer?tab=accueil', { replace: true }); }} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col" data-testid="easer-dashboard">
      <SEOHead title="Espace Easer" description="Espace employé Ptitservices." path="/easer" noindex />
      <header className="sticky top-0 z-40 bg-[hsl(var(--primary))] text-white shadow-md">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <img src={LOGO} alt="Ptitservices.com" className="h-8 w-auto rounded-md bg-white/90 p-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide opacity-80 leading-none">Espace Easer</p>
            <p className="font-semibold truncate leading-tight" data-testid="easer-user-name">{user.firstname || user.name}</p>
          </div>
          <Link to="/" aria-label="Retour au site" className="p-2 rounded-md hover:bg-white/10" data-testid="easer-home-link"><Home className="h-5 w-5" /></Link>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white" onClick={() => logout()} aria-label="Se déconnecter" data-testid="easer-logout"><LogOut className="h-5 w-5" /></Button>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-4 pb-24">
        {locked && (
          <div className={`rounded-xl border px-3 py-2.5 text-sm flex items-start gap-2 mb-4 ${overdue ? 'border-destructive/50 bg-destructive/10' : 'border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10'}`} role={overdue ? 'alert' : 'status'} data-testid="easer-active-lock">
            {overdue ? <BellRing className="h-4 w-4 mt-0.5 text-destructive flex-shrink-0 animate-pulse" /> : <Lock className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] flex-shrink-0" />}
            <div className="min-w-0">
              <p className="font-semibold">Prestation en cours{effectiveActive.since ? ` depuis ${fmtTime(effectiveActive.since)}` : ''} : {effectiveActive.label}{effectiveActive.client_name ? ` · ${effectiveActive.client_name}` : ''}{effectiveActive.local ? ' (début enregistré sur le téléphone)' : ''}</p>
              {overdue
                ? <p className="text-destructive font-medium" data-testid="easer-overdue-reminder">La fin prévue ({fmtTime(active.end)}) est dépassée de plus de 30 min : pensez à pointer la fin de prestation.</p>
                : <p className="text-muted-foreground">Seuls le compte-rendu et la fin de prestation sont disponibles. Pointez la fin pour retrouver le reste de l'espace Easer.</p>}
            </div>
          </div>
        )}
        {effectivePaused && !locked && (
          <div className={`rounded-xl border px-3 py-2.5 text-sm mb-4 ${pauseOverdue ? 'border-destructive/50 bg-destructive/10' : 'border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10'}`} role={pauseOverdue ? 'alert' : 'status'} data-testid="easer-pause-banner">
            <div className="flex items-start gap-2">
              {pauseOverdue ? <BellRing className="h-4 w-4 mt-0.5 text-destructive flex-shrink-0 animate-pulse" /> : <Coffee className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="font-semibold">Prestation en pause depuis {fmtTime(pausedSince)} : {pausedLabel}{effectivePaused.client_name ? ` · ${effectivePaused.client_name}` : ''}{effectivePaused.local ? ' (pause enregistrée sur le téléphone)' : ''}</p>
                {pauseOverdue
                  ? <p className="text-destructive font-medium" data-testid="easer-pause-overdue">Pause de plus de 30 min : reprenez la prestation, puis pointez sa fin une fois terminée.</p>
                  : <p className="text-muted-foreground">La fin a été pointée dans Dolibarr. Reprenez la prestation pour continuer (nouveau début), puis clôturez-la avec « Fin de prestation ».</p>}
              </div>
            </div>
            <Button onClick={resumeFromBanner} disabled={resuming} className="mt-2.5 w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="easer-pause-resume">
              {resuming ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />} Reprendre la prestation
            </Button>
          </div>
        )}
        <DolibarrStatusBanner />
        <AccBanner onOpenPlanning={tab === 'planning' ? null : () => setParams({ tab: 'planning' })} />
        <OfflineQueueBanner onUnauthorized={onUnauthorized} />
        <NotificationBanners items={banners} onOpen={() => setParams({ tab: 'grh', sub: 'notifications' })} onDismiss={(id) => setBannerHidden((h) => [...h, id])} />
        {tab !== 'accueil' && <InstallAppCard portal="easer" headOnly />}
        {tab === 'accueil' && <EaserHome user={user} active={effectiveActive} locked={locked} paused={effectivePaused} pauseOverdue={pauseOverdue} overdue={overdue}
          unreadNotifications={unreadNotifications} newDocs={newDocs} onUnauthorized={onUnauthorized} onGo={(t, sub) => setParams(sub ? { tab: t, sub } : { tab: t })} onResume={resumeFromBanner} resuming={resuming} />}
        {tab === 'planning' && <EaserPlanning user={user} onUnauthorized={onUnauthorized} active={effectiveActive} locked={locked} paused={effectivePaused} pauseUsed={pauseUsed}
          pendingEnds={pendingEnds} pendingStarts={pendingStarts.map((p) => p.eventId)} pendingPauses={pendingPauses.map((p) => p.eventId)} pendingResumes={pendingResumes.map((p) => p.eventId)} pendingClockIds={pendingClockIds} />}
        {tab === 'pointage' && <EaserPointage onUnauthorized={onUnauthorized} />}
        {tab === 'tracts' && <EaserTracts onUnauthorized={onUnauthorized} />}
        {tab === 'documents' && <EaserDocuments onUnauthorized={onUnauthorized} />}
        {tab === 'crm' && (
          <EaserCrm user={user} sub={crmSub} view={params.get('view') || ''} onUnauthorized={onUnauthorized}
            onNavigate={(sub, view) => setParams(view ? { tab: 'crm', sub, view } : { tab: 'crm', sub })} />
        )}
        {tab === 'grh' && <EaserGrh sub={grhSub} onSub={(id) => setParams({ tab: 'grh', sub: id })} onUnauthorized={onUnauthorized} notifications={notifications} unread={unreadNotifications} onNotificationRead={onNotificationRead} onGoHome={() => setParams({ tab: 'accueil' })} />}
      </main>
      {popupQueue && popupQueue.length > 0 && <NotificationPopup queue={popupQueue} onUnauthorized={onUnauthorized} onRead={onNotificationRead} />}

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 backdrop-blur-md" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} aria-label="Navigation espace Easer">
        <div className="max-w-2xl mx-auto grid" style={{ gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))` }}>
          {TABS.map((t) => {
            const current = tab === t.id;
            const disabled = locked && !ALLOWED_LOCKED.includes(t.id);
            return (
              <button key={t.id} type="button" onClick={() => pickTab(t.id)} aria-current={current ? 'page' : undefined} aria-disabled={disabled || undefined} data-testid={`easer-tab-${t.id}`}
                className={`relative flex flex-col items-center justify-center gap-0.5 min-h-[60px] text-[11px] font-semibold transition-colors ${current ? 'text-[hsl(var(--primary))]' : disabled ? 'text-muted-foreground/40 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground'}`}>
                <span className="relative">
                  {disabled ? <Lock className="h-5 w-5" /> : <t.icon className={`h-5 w-5 ${current ? 'scale-110' : ''} transition-transform`} />}
                  {t.id === 'documents' && newDocs > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-4 text-center ring-2 ring-background" aria-label={`${newDocs} nouveau${newDocs > 1 ? 'x' : ''} document${newDocs > 1 ? 's' : ''}`} data-testid="easer-tab-documents-badge">{newDocs > 9 ? '9+' : newDocs}</span>
                  )}
                  {t.id === 'grh' && unreadNotifications > 0 && (
                    <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold leading-4 text-center ring-2 ring-background" aria-label={`${unreadNotifications} notification${unreadNotifications > 1 ? 's' : ''} non lue${unreadNotifications > 1 ? 's' : ''}`} data-testid="easer-tab-grh-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>
                  )}
                </span>
                {t.label}
                {current && <span className="absolute bottom-0 h-0.5 w-10 rounded-full bg-[hsl(var(--primary))]" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
